import { useEffect, useState } from "react";
import { Building2, Mail, Trash2, Plus, Pencil, Package } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, euros, formatDate, mediaUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ImagePicker from "@/components/admin/ImagePicker";

const STATUSES = [
  { v: "nouveau", label: "Nouveau" },
  { v: "en_cours", label: "En cours" },
  { v: "traite", label: "Traité" },
];

const EMPTY_OFFER = { name: "", range_label: "", unit_price: 0, price_note: "", description: "", contents: "", image: "", order: 0, active: true };

function OffersTab() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/corporate-offers").then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...editing, unit_price: Number(editing.unit_price) || 0, order: Number(editing.order) || 0 };
      if (editing.id) await api.put(`/admin/corporate-offers/${editing.id}`, payload);
      else await api.post("/admin/corporate-offers", payload);
      toast.success("Offre enregistrée");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette offre ?")) return;
    await api.delete(`/admin/corporate-offers/${id}`);
    load();
  };

  const toggle = async (item) => {
    await api.put(`/admin/corporate-offers/${item.id}`, { ...item, active: !item.active });
    load();
  };

  if (!items) return <p className="text-muted-foreground">Chargement…</p>;

  return (
    <div data-testid="b2b-offers">
      <div className="flex justify-end mb-4">
        <button onClick={() => setEditing({ ...EMPTY_OFFER })} data-testid="offer-add"
          className="inline-flex items-center gap-2 bg-rouge text-white px-5 py-2.5 text-sm font-bold uppercase tracking-wide hover:bg-rouge-hover transition-colors">
          <Plus className="w-4 h-4" /> Ajouter une offre
        </button>
      </div>
      <div className="space-y-3" data-testid="offers-list">
        {items.map((o) => (
          <div key={o.id} className="bg-white border border-line p-5 flex items-center gap-4 flex-wrap" data-testid={`offer-row-${o.id}`}>
            {o.image ? <img src={mediaUrl(o.image)} alt="" className="w-10 h-10 object-cover" /> : <Package className="w-5 h-5 text-rouge shrink-0" />}
            <div className="flex-1 min-w-[200px]">
              <p className="font-display font-bold text-onyx">{o.name} {!o.active && <span className="text-xs font-sans text-muted-foreground">(désactivée)</span>}</p>
              <p className="text-sm text-muted-foreground">{o.range_label} · {o.price_note} {euros(o.unit_price)} TTC/unité</p>
            </div>
            <Switch checked={!!o.active} onCheckedChange={() => toggle(o)} data-testid={`offer-active-${o.id}`} />
            <button onClick={() => setEditing({ ...o })} data-testid={`offer-edit-${o.id}`} className="p-2 text-onyx hover:text-rouge" aria-label="Modifier"><Pencil className="w-4 h-4" /></button>
            <button onClick={() => remove(o.id)} data-testid={`offer-delete-${o.id}`} className="p-2 text-onyx hover:text-destructive" aria-label="Supprimer"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-white max-h-[90vh] overflow-y-auto" data-testid="offer-dialog">
          <DialogHeader><DialogTitle className="font-display">{editing?.id ? "Modifier" : "Ajouter"} une offre entreprise</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label>Nom de l'offre *</Label><Input data-testid="offer-name-input" className="mt-1.5" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
                <div><Label>Quantité (ex : 25–49 jeux)</Label><Input data-testid="offer-range-input" className="mt-1.5" value={editing.range_label} onChange={(e) => setEditing({ ...editing, range_label: e.target.value })} /></div>
                <div><Label>Prix unitaire (€ TTC)</Label><Input type="number" step="0.01" min="0" data-testid="offer-price-input" className="mt-1.5" value={editing.unit_price} onChange={(e) => setEditing({ ...editing, unit_price: e.target.value })} /></div>
                <div><Label>Préfixe prix (ex : « à partir de »)</Label><Input data-testid="offer-note-input" className="mt-1.5" value={editing.price_note} onChange={(e) => setEditing({ ...editing, price_note: e.target.value })} /></div>
              </div>
              <div><Label>Description</Label><Textarea rows={3} data-testid="offer-description-input" className="mt-1.5" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></div>
              <div><Label>Contenu de l'offre (une ligne par élément)</Label><Textarea rows={3} data-testid="offer-contents-input" className="mt-1.5" value={editing.contents} onChange={(e) => setEditing({ ...editing, contents: e.target.value })} /></div>
              <ImagePicker label="Image" value={editing.image} onChange={(url) => setEditing({ ...editing, image: url })} />
              <div className="grid grid-cols-2 gap-4 items-end">
                <div><Label>Ordre d'affichage</Label><Input type="number" data-testid="offer-order-input" className="mt-1.5" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} /></div>
                <div className="flex items-center gap-3 pb-1">
                  <Switch data-testid="offer-active-switch" checked={!!editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} />
                  <Label>Active</Label>
                </div>
              </div>
              <button onClick={save} disabled={saving || !editing.name} data-testid="offer-save"
                className="w-full bg-rouge text-white px-6 py-3 text-sm font-bold uppercase tracking-wide hover:bg-rouge-hover transition-colors disabled:opacity-50">
                {saving ? "Enregistrement…" : "Enregistrer"}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function CorporateAdmin() {
  const [inquiries, setInquiries] = useState(null);
  const [messages, setMessages] = useState(null);

  const load = () => {
    api.get("/admin/corporate").then((r) => setInquiries(r.data)).catch((e) => toast.error(apiError(e)));
    api.get("/admin/messages").then((r) => setMessages(r.data)).catch((e) => toast.error(apiError(e)));
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    await api.put(`/admin/corporate/${id}`, { status });
    toast.success("Statut mis à jour");
    load();
  };
  const removeInquiry = async (id) => {
    if (!window.confirm("Supprimer cette demande ?")) return;
    await api.delete(`/admin/corporate/${id}`);
    load();
  };
  const toggleRead = async (m) => {
    await api.put(`/admin/messages/${m.id}`, { read: !m.read });
    load();
  };
  const removeMessage = async (id) => {
    if (!window.confirm("Supprimer ce message ?")) return;
    await api.delete(`/admin/messages/${id}`);
    load();
  };

  return (
    <div data-testid="admin-corporate">
      <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight uppercase text-onyx mb-8">Entreprises</h1>

      <Tabs defaultValue="offers">
        <TabsList className="bg-white border border-line mb-6 flex-wrap h-auto">
          <TabsTrigger value="offers" data-testid="tab-offers">Offres B2B</TabsTrigger>
          <TabsTrigger value="b2b" data-testid="tab-b2b">Demandes de devis</TabsTrigger>
          <TabsTrigger value="messages" data-testid="tab-messages">Messages contact</TabsTrigger>
        </TabsList>

        <TabsContent value="offers"><OffersTab /></TabsContent>

        <TabsContent value="b2b">
          {!inquiries ? <p className="text-muted-foreground">Chargement…</p> : inquiries.length === 0 ? (
            <div className="bg-white border border-line p-10 text-center" data-testid="b2b-empty">
              <Building2 className="w-8 h-8 text-rouge mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">Aucune demande entreprise pour le moment.</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="b2b-list">
              {inquiries.map((q) => (
                <div key={q.id} className="bg-white border border-line p-5" data-testid={`b2b-row-${q.id}`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <p className="font-display font-bold text-onyx">{q.company} — {q.quantity} boîtes</p>
                      <p className="text-sm text-muted-foreground">{q.contact_name} · <a href={`mailto:${q.email}`} className="text-rouge hover:underline">{q.email}</a>{q.phone ? ` · ${q.phone}` : ""}</p>
                      <p className="text-xs text-muted-foreground mt-1">{formatDate(q.created_at)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <select value={q.status} onChange={(e) => setStatus(q.id, e.target.value)} data-testid={`b2b-status-${q.id}`}
                        className="border border-input bg-white px-2 py-1.5 text-sm">
                        {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
                      </select>
                      <button onClick={() => removeInquiry(q.id)} data-testid={`b2b-delete-${q.id}`} className="p-2 text-onyx hover:text-destructive" aria-label="Supprimer"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                  {q.message && <p className="mt-3 text-sm text-foreground/80 bg-sand p-3">{q.message}</p>}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="messages">
          {!messages ? <p className="text-muted-foreground">Chargement…</p> : messages.length === 0 ? (
            <div className="bg-white border border-line p-10 text-center" data-testid="messages-empty">
              <Mail className="w-8 h-8 text-rouge mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">Aucun message pour le moment.</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="messages-list">
              {messages.map((m) => (
                <div key={m.id} className={`bg-white border p-5 ${m.read ? "border-line" : "border-rouge"}`} data-testid={`message-row-${m.id}`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <p className="font-display font-bold text-onyx">{m.name} — {m.subject || "Contact"}</p>
                      <p className="text-sm text-muted-foreground"><a href={`mailto:${m.email}`} className="text-rouge hover:underline">{m.email}</a> · {formatDate(m.created_at)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => toggleRead(m)} data-testid={`message-read-${m.id}`}
                        className="text-xs font-bold uppercase tracking-wide px-3 py-1.5 border border-line hover:border-rouge text-onyx">
                        {m.read ? "Marquer non lu" : "Marquer lu"}
                      </button>
                      <button onClick={() => removeMessage(m.id)} data-testid={`message-delete-${m.id}`} className="p-2 text-onyx hover:text-destructive" aria-label="Supprimer"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                  <p className="mt-3 text-sm text-foreground/80 bg-sand p-3 whitespace-pre-wrap">{m.message}</p>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
